import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { PendingToolApproval } from "@/data/use-messages-data";
import { useState } from "react";

type ToolApprovalCardProps = {
  approval: PendingToolApproval;
  onSubmit: (params: {
    decision: "approve" | "deny";
    note?: string;
  }) => Promise<void>;
};

export function ToolApprovalCard({
  approval,
  onSubmit,
}: ToolApprovalCardProps) {
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const skillName =
    typeof approval.args.name === "string" ? approval.args.name : undefined;
  const metadataDescription =
    typeof approval.args.metadataDescription === "string"
      ? approval.args.metadataDescription
      : undefined;

  const title =
    approval.toolName === "activate_skill"
      ? skillName
        ? `Activate skill: ${skillName}?`
        : "Activate this skill?"
      : `Run ${approval.toolName}?`;

  async function handleDecision(decision: "approve" | "deny") {
    setIsSubmitting(true);
    try {
      await onSubmit({
        decision,
        note: note.trim() || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="bg-background flex flex-col gap-3 rounded-lg border p-3 text-sm">
      <div className="font-medium">{title}</div>
      {approval.toolName === "activate_skill" && metadataDescription && (
        <p className="text-foreground-3 text-xs">{metadataDescription}</p>
      )}
      <Textarea
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Optional note (included with Yes, or as a reason with No)"
        rows={2}
        disabled={isSubmitting}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => handleDecision("approve")}
          disabled={isSubmitting}
        >
          Yes
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleDecision("deny")}
          disabled={isSubmitting}
        >
          No
        </Button>
      </div>
    </div>
  );
}
